import { Link } from "react-router-dom";

/**
 * Trust & Security
 *
 * Static, content-only page (no backend call). It summarises the portal's
 * trust posture for client procurement and security reviews. Copy is kept
 * factual: it describes how the platform is built and operated, and does not
 * claim formal certifications (such as ISO 27001 or SOC 2) the product does
 * not hold.
 *
 * The privacy contact line is a placeholder and is marked as configurable so
 * it can be set per deployment at onboarding.
 */
function IconInfo() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M12 11v5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
      <circle cx="12" cy="7.75" r="1" fill="currentColor" />
    </svg>
  );
}

export function Trust() {
  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Trust &amp; Security</h1>
        <p className="page-subtitle">
          How the iQuest portal is hosted, secured, and operated. This page is
          provided to support your procurement and security reviews.
        </p>
      </div>

      <div className="info-banner info" style={{ marginBottom: 20 }}>
        <IconInfo />
        <div>
          This page describes how the platform is built and run. It does not
          claim formal certifications (such as ISO 27001 or SOC 2). If your
          review needs evidence beyond what is shown here, contact us using the
          details below.
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <h2 className="card-title">Hosting and data residency</h2>
        </div>
        <div className="section-subhead">Region</div>
        <p>
          The portal runs on Microsoft Azure in an Australian region. Customer
          data is stored in-country, with data residency kept within Australia.
        </p>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <h2 className="card-title">Security posture</h2>
        </div>
        <ul style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 8 }}>
          <li>
            <strong>Single sign-on:</strong> authentication is handled by
            Microsoft Entra ID, which supports multi-factor authentication (MFA).
          </li>
          <li>
            <strong>Tenant isolation:</strong> client data is separated two
            ways. Application queries are scoped to your company, and PostgreSQL
            row-level security policies enforce the same boundary underneath as a
            second layer.
          </li>
          <li>
            <strong>Audit logging:</strong> administrative actions (such as
            configuration changes, role grants, and approval decisions) are
            written to an immutable audit log with the actor, timestamp, and the
            change recorded.
          </li>
          <li>
            <strong>Encryption:</strong> data is encrypted in transit and at
            rest.
          </li>
          <li>
            <strong>Least-privilege access:</strong> access is granted on a
            least-privilege basis, scoped to what each role needs.
          </li>
        </ul>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <h2 className="card-title">Privacy and data protection</h2>
        </div>
        <ul style={{ margin: 0, paddingLeft: 20, display: "grid", gap: 8 }}>
          <li>
            <strong>Australian Privacy Act:</strong> the portal is designed to
            align with the Australian Privacy Act and the Australian Privacy
            Principles.
          </li>
          <li>
            <strong>Self-service data export:</strong> you can export your own
            account data at any time from{" "}
            <Link to="/me/preferences">My Preferences</Link>.
          </li>
          <li>
            <strong>Self-service account deletion:</strong> you can request
            deletion of your account from{" "}
            <Link to="/me/preferences">My Preferences</Link>. The request status
            is shown explicitly once submitted.
          </li>
          <li>
            <strong>Use of your data:</strong> customer data is not sold and is
            not shared with third parties for their own purposes.
          </li>
        </ul>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Privacy and DPIA contact</h2>
        </div>
        <p>
          For privacy enquiries, data protection impact assessments (DPIA), or
          security questionnaires, contact our privacy team.
        </p>
        <p style={{ marginTop: 8 }}>
          <a href="mailto:privacy@iquest.com.au">privacy@iquest.com.au</a>
        </p>
        <p className="empty-state-blurb" style={{ marginTop: 8 }}>
          Contact details shown here are a placeholder and are configurable per
          deployment. Confirm the correct address with your iQuest
          representative at onboarding.
        </p>
      </div>
    </>
  );
}
